import { BaseSaver, SAVE_DATA_KEY } from "./BaseSaver";
import { Data, FBInstant } from "./types";

export interface IFBInstantSaverParams {
  setDataAsync: typeof FBInstant.player.setDataAsync;
  getDataAsync: typeof FBInstant.player.getDataAsync;
  flushDataAsync: typeof FBInstant.player.flushDataAsync;
  throttleMS?: number;
}

export class FBInstantSaver<T extends Data> extends BaseSaver<T> {
  protected _cache: object = {};

  protected _throttlePromise: Promise<void> = null;
  protected _throttleMS: number;

  private saveQueued: boolean = false;
  private readonly _setDataAsyncRef: typeof FBInstant.player.setDataAsync;
  private readonly _getDataAsyncRef: typeof FBInstant.player.getDataAsync;
  private readonly _flushDataAsyncRef: typeof FBInstant.player.flushDataAsync;
  private loadedKeys: string[] = [];

  public constructor(params: IFBInstantSaverParams) {
    super();

    this._setDataAsyncRef = params.setDataAsync;
    this._getDataAsyncRef = params.getDataAsync;
    this._flushDataAsyncRef = params.flushDataAsync;
    this._throttleMS = params.throttleMS ?? 7500;
  }

  public async clear(): Promise<void> {
    const clearData: object = this.loadedKeys.reduce((acc, k) => {
      acc[k] = null;
      return acc;
    }, {} as Record<string, null>);

    this.logger.debugLog("Calling setDataAsync...");
    await this._setDataAsyncRef(clearData);
    this.logger.debugLog("Calling flushDataAsync...");
    await this._flushDataAsyncRef();

    return;
  }

  public load(keys: string[]): Promise<T> {
    this.logger.debugLog("Calling getDataAsync...");
    this.loadedKeys = keys;

    return (this._getDataAsyncRef(keys) as Promise<T>).catch((error: Error) => {
      this.logger.error(error);
      return this.load(keys);
    });
  }

  public async save(data: Partial<T>): Promise<void> {
    this.logger.debugLog("Updating cache...");
    this.updateCache(data);

    if (this.saveQueued) {
      this.logger.debugLog("Save already queued; throttling...");
      return this.throttle();
    } else {
      this.saveQueued = true;
      this.logger.debugLog("Save queued; throttling...");
      await this.throttle();
      this.saveQueued = false;
    }

    const cacheCopy: object = JSON.parse(JSON.stringify(this._cache));

    return new Promise<void>(async (resolve) => {
      // fixme add error handling
      this.logger.debugLog("Calling setDataAsync...");
      await this._setDataAsyncRef(cacheCopy);
      this.logger.debugLog("Calling flushDataAsync...");
      await this._flushDataAsyncRef();
      this.logger.debugLog("Saved successfully.");
      resolve();
    });
  }

  public get saveDataKey(): string {
    return SAVE_DATA_KEY;
  }

  public get enabled(): boolean {
    return true;
  }

  protected throttle(): Promise<void> {
    // tslint:disable-next-line:no-promise-as-boolean
    if (this._throttlePromise) return this._throttlePromise;

    return (this._throttlePromise = new Promise<void>((resolve) => {
      setTimeout(() => {
        this._throttlePromise = null;
        resolve();
      }, this._throttleMS);
    }));
  }
}
